import type { ComponentProps } from 'react';

import { cva, type VariantProps } from 'class-variance-authority';

import { useFieldWiring } from '../hooks/use-field-wiring';
import { cn } from '../lib/utils';
import { Checkbox } from './checkbox';
import {
    Field,
    FieldContent,
    FieldLabel,
    type FieldOverrides,
    FieldSlot,
    FieldTitle,
} from './field';

const checkboxFieldVariants = cva('gap-2', {
    variants: {
        variant: {
            default: '',
            box: cn(
                'rounded-sm p-3 transition-colors',
                'data-[disabled=true]:cursor-not-allowed',
                'not-data-[disabled=true]:has-[[data-state=checked]]:border-primary not-data-[disabled=true]:has-[[data-state=checked]]:bg-primary/10',
                'not-data-[disabled=true]:hover:bg-accent/50',
                'not-data-[disabled=true]:has-[[data-state=checked]]:hover:bg-accent/50',
                'has-[:focus-visible]:border-ring has-[:focus-visible]:[box-shadow:0_0_0_2px_var(--accent)]',
                'data-[invalid=true]:border-destructive data-[invalid=true]:bg-destructive-foreground',
            ),
        },
    },
    defaultVariants: {
        variant: 'default',
    },
});

type Props = ComponentProps<typeof Checkbox> &
    VariantProps<typeof checkboxFieldVariants> & {
        label: string;
        labelClassName?: string;
        description?: string;
        error?: boolean | string;
        fieldProps?: FieldOverrides;
        fieldClassName?: string;
    };

function CheckboxField({
    label,
    labelClassName,
    description,
    error,
    id,
    fieldProps,
    fieldClassName,
    variant,
    ...checkboxProps
}: Props) {
    const wiring = useFieldWiring({ id, description, error });
    const { className: fieldPropsClassName, ...restFieldProps } = fieldProps ?? {};
    const isBox = variant === 'box';

    const field = (
        <Field
            orientation="horizontal"
            data-disabled={checkboxProps.disabled}
            data-invalid={wiring.dataInvalid}
            className={cn(
                'group',
                checkboxFieldVariants({ variant }),
                fieldPropsClassName,
                fieldClassName,
            )}
            {...restFieldProps}
        >
            <Checkbox expandedHitArea {...checkboxProps} {...wiring.controlProps} />
            <FieldContent>
                {isBox ? (
                    <FieldTitle>{label}</FieldTitle>
                ) : (
                    <FieldLabel
                        htmlFor={wiring.controlId}
                        dimWhenDisabled
                        className={cn(
                            'cursor-pointer group-data-[disabled=true]/field:cursor-not-allowed',
                            labelClassName,
                        )}
                    >
                        {label}
                    </FieldLabel>
                )}
                <FieldSlot
                    description={description}
                    descriptionId={wiring.descriptionId}
                    errorMessage={wiring.errorMessage}
                    errorId={wiring.errorId}
                />
            </FieldContent>
        </Field>
    );

    if (isBox) {
        return (
            <FieldLabel
                className={cn(
                    'block cursor-pointer has-[[data-disabled=true]]:cursor-not-allowed',
                    labelClassName,
                )}
            >
                {field}
            </FieldLabel>
        );
    }

    return field;
}

export { CheckboxField, checkboxFieldVariants };
